import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { flattenedProxyMapState, vdparFundIdsState, useResetAsOfDateOnSubjectChange } from 'venn-state';

import { autoGroupColumnDefStaticConstants, BOLD_CLASS } from '../../../studio-blocks';
import { useIsBlockGridCompact } from '../../../studio-blocks/logic/useIsBlockGridCompact';
import { useCommonTreeGridProps } from '../../../studio-blocks/logic/columnUtils';
import type { AllocationRowData } from '../../../studio-blocks/logic/usePortfolioComparisonUtils';
import ValueCellRenderer from '../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { getDefaultValueFormat } from '../../../studio-blocks/logic/gridStyling';
import type { CellClassParams, ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import { usePortfolioComparison } from '../../../studio-blocks/logic/usePortfolioComparison';
import { usePortfolioGridDisplaySettings } from '../../../studio-blocks/logic/usePortfolioGridDisplaySettings';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import { useLabelColumnWidth } from '../../../studio-blocks/logic/useLabelColumnMaxWidth';
import { OptionIcon } from '../../../common/OptionIcon';
import {
  renderSuperscripts,
  usePageProxySymbol,
  usePageVdparSymbol,
} from '../../components/core/block-subject-footnotes-and-superscripts';
import { useHasFF } from 'venn-utils';

interface PortfolioComparisonContentProps {
  id: string;
}

const useAutoGroupColumnDef = (columnDefs: ColGroupDef<AllocationRowData>[]): ColDef => {
  const { maxWidth, minWidth } = useLabelColumnWidth(columnDefs);
  const proxyMap = useRecoilValue(flattenedProxyMapState);
  const proxySymbol = usePageProxySymbol();
  const vdparFundIds = useRecoilValue(vdparFundIdsState);
  const vdparSymbol = usePageVdparSymbol();

  return useMemo(
    () => ({
      ...autoGroupColumnDefStaticConstants,
      autoHeight: true,
      minWidth,
      maxWidth,
      cellRendererParams: {
        ...autoGroupColumnDefStaticConstants.cellRendererParams,
        innerRendererParams: { editable: true },
      },
      cellRenderer: ({ data, value }: ICellRendererParams<AllocationRowData>) => {
        const icon = data?.optionData ? <OptionIcon optionType={data.optionData.optionType} /> : null;
        return (
          <>
            {value}
            {icon && <> {icon}</>}
            {renderSuperscripts([
              { condition: !!data?.fundId && !!proxyMap?.[data.fundId], symbol: proxySymbol },
              { condition: !!data?.fundId && vdparFundIds.has(data.fundId), symbol: vdparSymbol },
            ])}
          </>
        );
      },
    }),
    [maxWidth, minWidth, proxyMap, proxySymbol, vdparFundIds, vdparSymbol],
  );
};

const treeGridOverrides: ColDef<AllocationRowData> = {
  cellRenderer: ValueCellRenderer,
  valueFormatter: getDefaultValueFormat,
  cellClassRules: {
    [BOLD_CLASS]: (params: CellClassParams<AllocationRowData>) => !!params.data?.isStrategy,
  },
};

export const PortfolioComparisonContent = ({ id }: PortfolioComparisonContentProps) => {
  const isCompact = useIsBlockGridCompact();
  const skipKSuffixInFormatting = useHasFF('skip_k_suffix_in_formatting_ff');

  const extraDefaultColDef = {
    ...treeGridOverrides,
    ...{ cellRendererParams: { skipKSuffixInFormatting } },
  };
  const commonGridProps = useCommonTreeGridProps(extraDefaultColDef);

  const { rowData, columnDefs, onCellValueChanged, pinnedBottomRowData } = usePortfolioComparison(id);
  const shouldExpand = usePortfolioGridDisplaySettings(rowData, columnDefs);
  const autoGroupColumnDef = useAutoGroupColumnDef(columnDefs);

  useResetAsOfDateOnSubjectChange({ blockId: id, numSubjects: 2 });

  return (
    <ExportableGrid
      {...commonGridProps}
      exportable
      autoGroupColumnDef={autoGroupColumnDef}
      selectedRefId={id}
      isCompact={isCompact}
      rowData={rowData}
      columnDefs={columnDefs}
      shouldExpand={shouldExpand}
      onCellValueChanged={onCellValueChanged}
      pinnedBottomRowData={pinnedBottomRowData}
      treeData
    />
  );
};
